// Copyright 2025. WebPros International GmbH. All rights reserved.

import { PageHeader } from 'admin/common/components/PageHeader/PageHeader';
import {
    useEffect,
    useState,
} from 'react';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import {
    computeResources,
    IComputeResourceResponse,
} from 'common/api/resources/ComputeResource';
import { readiness } from 'common/api/resources/DisasterRecovery';
import { IComputeResourceRecoveryReadinessStatusResponse } from 'common/api/resources/DisasterRecovery/model';
import DestinationComputeResourcesTable
    from 'admin/disasterRecovery/components/DestinationComputeResourcesTable/DestinationComputeResourcesTable';
import BackupCoverageTable from 'admin/disasterRecovery/components/BackupCoverageTable/BackupCoverageTable';
import AvailableResources from 'admin/disasterRecovery/components/AvailableResources/AvailableResources';
import { Loader } from 'common/components';
import {
    Alert,
    Heading,
    Translate,
} from '@plesk/ui-library';
import BackupNodesTable from 'admin/disasterRecovery/components/BackupNodesTable/BackupNodesTable';
import {
    backupNodes,
    IBackupNodeResponse,
} from 'common/api/resources/BackupNode';

interface IRouteProps {
    computeResourceId: string;
}

type IProps = RouteComponentProps<IRouteProps>;

export function ComputeResourceRecoveryReadinessStatus({
    match: {
        params: {
            computeResourceId,
        },
    },
}: IProps) {

    const [readinessStatus, setReadinessStatus] = useState<IComputeResourceRecoveryReadinessStatusResponse>();

    const [computeResource, setComputeResource] = useState<IComputeResourceResponse>();

    const [computeResourceLoading, setComputeResourceLoading] = useState(true);

    const [readinessStatusLoading, setReadinessStatusLoading] = useState(true);

    const [crBackupNodes, setCrBackupNodes] = useState<IBackupNodeResponse[]>([]);

    const [backupNodesLoading, setBackupNodesLoading] = useState(true);

    useEffect(() => {
        const getData = async () => {
            const response = await computeResources.item(+computeResourceId);

            setComputeResourceLoading(false);

            setComputeResource(response.data.data);
        };

        getData();
    }, [computeResourceId]);

    useEffect(() => {
        const getData = async () => {
            const response = await readiness
                .computeResourceReadiness(computeResourceId);

            setReadinessStatusLoading(false);

            setReadinessStatus(response.data.data);
        };

        getData();
    }, [computeResourceId]);

    useEffect(() => {
        const getData = async () => {
            const res = await backupNodes.list({
                filters: {
                    compute_resource_id: computeResourceId,
                },
                per_page: 'all',
            });

            setBackupNodesLoading(false);

            setCrBackupNodes(res.data.data);
        };

        getData();
    }, [computeResourceId]);

    const loading = computeResourceLoading || readinessStatusLoading || backupNodesLoading;

    return (
        <Loader isLoading={loading}>
            <PageHeader
                title={<Translate content="disasterRecovery.readinessStatus.title" />}
                isButtonShown={false}
            />

            <Heading level={2}>
                {computeResource?.name}
            </Heading>

            {readinessStatus && readinessStatus.ready_for_recovery && (
                <Alert intent={'success'}>
                    <Translate content="disasterRecovery.readinessStatus.successAlert" />
                </Alert>
            )}

            {readinessStatus && !readinessStatus.ready_for_recovery && readinessStatus.destination_compute_resources.length && (
                <Alert intent={'danger'}>
                    <Translate content="disasterRecovery.readinessStatus.errorAlertWithCrList" />
                </Alert>
            )}

            {readinessStatus && !readinessStatus.ready_for_recovery && !readinessStatus.destination_compute_resources.length && (
                <Alert intent={'danger'}>
                    <Translate content="disasterRecovery.readinessStatus.errorAlertWithoutCrList" />
                </Alert>
            )}

            <DestinationComputeResourcesTable
                computeResourcesWithCompatibilityDetails={readinessStatus?.destination_compute_resources ?? []}
            />

            <BackupCoverageTable
                backupCoverage={readinessStatus?.backup_coverage}
            />

            <BackupNodesTable backupNodes={crBackupNodes} />

            <AvailableResources
                requireResources={readinessStatus?.require_resources}
                availableResources={readinessStatus?.available_resources}
            />
        </Loader>
    );
}

export default withRouter(ComputeResourceRecoveryReadinessStatus);
